import { useEffect, useState, useMemo } from "react";
import { Header } from "../components/Header";
import { getStateRules } from "../lib/api";
import { toast } from "sonner";
import { Scales, MagnifyingGlass, Spinner, CalendarBlank, ShieldCheck } from "@phosphor-icons/react";

export default function StateLaws() {
  const [rules, setRules] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    getStateRules().then(setRules).catch(() => {
      toast.error("Failed to load state rules.");
      setRules([]);
    });
  }, []);

  const filtered = useMemo(() => {
    if (!rules) return [];
    const s = q.trim().toLowerCase();
    if (!s) return rules;
    return rules.filter((r) =>
      r.state_name.toLowerCase().includes(s) || r.state.toLowerCase().includes(s)
    );
  }, [rules, q]);

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-gray-200 flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2">
              <Scales size={16} /> 50-State Reference
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-white mt-1">
              State Diminished-Value Laws
            </h1>
            <p className="text-sm text-gray-400 mt-1 max-w-2xl">
              Property-damage claim deadlines and how each state treats diminished value. General
              reference only — verify with a local attorney.
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              data-testid="state-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search state…"
              className="w-full pl-9 pr-3 py-2.5 text-sm bg-[#141417] border border-white/10 rounded-md focus:ring-1 focus:ring-[#C5A059] focus:border-[#C5A059] text-white placeholder:text-gray-600 outline-none"
            />
          </div>
        </div>

        {rules === null ? (
          <div className="flex items-center justify-center py-24 text-[#C5A059]">
            <Spinner size={28} className="animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-500">No states match "{q}".</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="state-laws-grid">
            {filtered.map((r) => (
              <div
                key={r.state}
                data-testid={`state-card-${r.state}`}
                className="bg-[#0F0F11] border border-white/10 rounded-xl p-5 shadow-2xl hover:border-[#C5A059]/40 transition-colors flex flex-col"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-display text-lg font-bold text-white leading-tight">{r.state_name}</h3>
                    <span className="text-[11px] font-mono-num text-gray-500">{r.state}</span>
                  </div>
                  <span className="px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider bg-[#C5A059]/10 text-[#E5C17B] border border-[#C5A059]/30 rounded-sm whitespace-nowrap">
                    {r.dv_recognition}
                  </span>
                </div>
                <div className="mt-4 flex items-center gap-3 bg-black/40 border border-white/10 rounded-lg p-3">
                  <CalendarBlank size={22} className="text-[#C5A059] shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500">Claim deadline</div>
                    <div className="text-sm font-semibold text-gray-100">
                      <span className="font-mono-num text-[#E5C17B]">{r.property_damage_sol_years}</span>{" "}
                      year{r.property_damage_sol_years !== 1 ? "s" : ""} statute of limitations
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-start gap-2 text-xs text-gray-400 leading-relaxed">
                  <ShieldCheck size={16} className="text-gray-500 shrink-0 mt-0.5" />
                  <span>
                    {r.dv_recognition === "First & Third Party"
                      ? "Recognizes both first-party and third-party DV claims (e.g. GA / Mabry v. State Farm)."
                      : "Third-party DV claims recognized; first-party DV is often limited by policy language — review your policy."}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-[11px] text-gray-600 italic mt-8 max-w-3xl">
          {rules && rules[0]?.disclaimer}
        </p>
      </main>
    </div>
  );
}
